import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { StateView } from "./components/StateView";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WorkspaceProvider, useWorkspace } from "./contexts/WorkspaceContext";
import { DailyPage } from "./pages/DailyPage";
import { LoginPage } from "./pages/LoginPage";
import { OverviewPage } from "./pages/OverviewPage";
import { UnauthorizedPage } from "./pages/UnauthorizedPage";
import { chooseInitialShop, isOwnedShopId, lastShopKey } from "./lib/access";

function HomeGate() {
  const { identity } = useAuth();
  const { shops, loading, error, reload } = useWorkspace();
  if (!identity) return <Navigate to="/login" replace />;
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} retry={reload} />;
  if (shops.length === 0) return <Navigate to="/not-authorized" replace />;
  const key = lastShopKey(identity.getPrincipal().toText());
  const remembered = localStorage.getItem(key);
  const selected = chooseInitialShop(shops, remembered)!;
  return <Navigate to={`/shops/${selected.id.toText()}/overview`} replace />;
}

function ProtectedShop() {
  const { identity } = useAuth();
  const { shopId = "" } = useParams();
  const { shops, actor, activeShop, loading, error, selectShop, reload } = useWorkspace();
  const navigate = useNavigate();

  useEffect(() => {
    if (!identity || loading || error || shops.length === 0) return;
    if (!isOwnedShopId(shops, shopId)) {
      navigate("/not-authorized", { replace: true });
      return;
    }
    if (activeShop?.id.toText() !== shopId || !actor) {
      void selectShop(shopId).then((outcome) => {
        if (outcome === "denied") navigate("/not-authorized", { replace: true });
      });
    }
  }, [identity, loading, error, shops, shopId, activeShop, actor, selectShop, navigate]);

  if (!identity) return <Navigate to="/login" replace />;
  if (error) return <ErrorState message={error} retry={reload} />;
  if (loading || activeShop?.id.toText() !== shopId || !actor) return <LoadingState />;
  return <Outlet />;
}

function LoadingState() {
  return <StateView eyebrow="Owner workspace" title="Preparing your reports"><p>Checking your shops and secured reporting access.</p><div className="progress"><span /></div></StateView>;
}

function ErrorState({ message, retry }: { message: string; retry: () => Promise<void> }) {
  return <StateView eyebrow="Connection issue" title="We could not load the workspace" action={<button className="primary-button" onClick={() => void retry()}>Retry</button>}><p>{message}</p></StateView>;
}

function AppRoutes() {
  const { loading } = useAuth();
  if (loading) return <LoadingState />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/not-authorized" element={<UnauthorizedPage />} />
      <Route path="/" element={<HomeGate />} />
      <Route element={<ProtectedShop />}>
        <Route element={<AppShell />}>
          <Route path="/shops/:shopId/overview" element={<OverviewPage />} />
          <Route path="/shops/:shopId/daily" element={<DailyPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return <BrowserRouter><AuthProvider><WorkspaceProvider><AppRoutes /></WorkspaceProvider></AuthProvider></BrowserRouter>;
}
